// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { MessageModel } from "@insite/client-framework/Types/ApiModels";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import ArrowRight from "@insite/mobius/Icons/ArrowRight";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getRelativeNotificationTime from "@store/Library/Components/Notifications/getRelativeNotificationTime";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import React, { FC } from "react";
import { css } from "styled-components";

export interface NotificationItemPresentationProps extends ExpandStyles<NotificationItemStyles> {}

export interface NotificationItemProps extends NotificationItemPresentationProps {
    message: MessageModel;
    /** Session language, for localized relative time. */
    language?: string;
    /** Toggle this notification's read status. */
    onToggleRead: (message: MessageModel) => void;
    /** Called when the action link is clicked (before navigation) — used to close the flyout/panel. */
    onActionClick?: () => void;
    /**
     * Page type appended to the action link as `returnPage`, so the target page (e.g. TEC Contact
     * Support) can render a "back" link to wherever the notification was opened from. Preserves the
     * legacy AccountMessages behavior now that the flyout is opened globally rather than from the
     * My Account Messages page.
     */
    actionReturnPageType?: string;
}

export interface NotificationItemStyles {
    wrapper?: InjectableCss;
    unreadDot?: InjectableCss;
    content?: InjectableCss;
    titleRow?: InjectableCss;
    titleGroup?: InjectableCss;
    typeIcon?: IconPresentationProps;
    title?: TypographyPresentationProps;
    time?: TypographyPresentationProps;
    body?: TypographyPresentationProps;
    actionRow?: InjectableCss;
    actionLink?: LinkPresentationProps;
    markReadLink?: LinkPresentationProps;
}

export const notificationItemStyles: NotificationItemStyles = {
    wrapper: {
        css: css`
            display: flex;
            align-items: flex-start;
            gap: ${pxToRem(10)};
            /* Per-item padding (not a container padding) so the divider below spans edge to edge. */
            padding: ${pxToRem(16, 23)};
            border-bottom: 1px solid ${getColor("common.borderLight")};

            &:last-child {
                border-bottom: 0;
            }
        `,
    },
    unreadDot: {
        css: css`
            flex: 0 0 auto;
            width: ${pxToRem(8)};
            height: ${pxToRem(8)};
            margin-top: ${pxToRem(7)};
            border-radius: 50%;
            background-color: ${getColor("tertiary.light")};
        `,
    },
    content: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(4)};
            min-width: 0;
            flex: 1 1 auto;
        `,
    },
    titleRow: {
        css: css`
            display: flex;
            align-items: flex-start;
            gap: ${pxToRem(8)};
        `,
    },
    titleGroup: {
        css: css`
            display: flex;
            align-items: center;
            gap: ${pxToRem(6)};
            min-width: 0;
            flex: 1 1 auto;
        `,
    },
    typeIcon: {
        size: 20,
    },
    title: {
        weight: "bold",
    },
    time: {
        css: css`
            flex: 0 0 auto;
            font-size: ${x => x.theme.design.typography.size.smallest};
            color: ${getColor("text.light")};
            white-space: nowrap;
            margin-top: ${pxToRem(2)};
        `,
    },
    body: {
        forwardAs: "p",
        css: css`
            margin: 0;
            font-size: ${x => x.theme.design.typography.size.smaller};
            line-height: ${x => x.theme.design.typography.lineHeight.base};
            color: ${getColor("text.light")};
        `,
    },
    actionRow: {
        css: css`
            /* Bottom row: optional action link on the left, mark-as-read link pinned right. */
            display: flex;
            align-items: center;
            gap: ${pxToRem(12)};
            margin-top: ${pxToRem(2)};
        `,
    },
    actionLink: {
        color: "text.link",
        icon: {
            iconProps: {
                src: ArrowRight,
                size: 16,
                css: css`
                    margin-left: ${pxToRem(2)};
                `,
            },
            position: "right",
        },
        css: css`
            font-size: ${x => x.theme.design.typography.size.smaller};
            font-weight: ${x => x.theme.design.typography.weight.medium};
        `,
    },
    markReadLink: {
        color: "text.link",
        css: css`
            /* Pinned right regardless of whether an action link is present. */
            margin-left: auto;
            flex: 0 0 auto;
            white-space: nowrap;
            font-size: ${x => x.theme.design.typography.size.smaller};
            font-weight: ${x => x.theme.design.typography.weight.medium};
        `,
    },
};

const NotificationItem: FC<NotificationItemProps> = ({
    message,
    language,
    onToggleRead,
    onActionClick,
    actionReturnPageType,
    extendedStyles,
    overridedStyles,
}) => {
    const styles = useExpandProps(notificationItemStyles, extendedStyles, overridedStyles);

    const typeIcon = message.properties?.icon;
    const actionText = message.properties?.linkText;
    const actionHref = message.properties?.linkValue;
    // Pick the right separator: linkValue is not guaranteed to already carry a query string
    // (today's TEC link does, but this renders every notification type).
    const resolvedActionHref =
        actionHref && actionReturnPageType
            ? `${actionHref}${actionHref.includes("?") ? "&" : "?"}returnPage=${actionReturnPageType}`
            : actionHref;
    const relativeTime = getRelativeNotificationTime(message.dateToDisplay, language);

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="notifications_item">
            {!message.isRead && <StyledWrapper {...styles.unreadDot} aria-hidden="true" />}
            <StyledWrapper {...styles.content}>
                <StyledWrapper {...styles.titleRow}>
                    <StyledWrapper {...styles.titleGroup}>
                        {typeIcon && <Icon {...styles.typeIcon} src={typeIcon} />}
                        <Typography {...styles.title}>{message.subject}</Typography>
                    </StyledWrapper>
                    {relativeTime && <Typography {...styles.time}>{relativeTime}</Typography>}
                </StyledWrapper>
                <Typography {...styles.body}>{message.body}</Typography>
                <StyledWrapper {...styles.actionRow}>
                    {actionText && resolvedActionHref && (
                        <Link {...styles.actionLink} href={resolvedActionHref} onClick={onActionClick}>
                            {translate(`${actionText}`)}
                        </Link>
                    )}
                    <Link
                        {...styles.markReadLink}
                        onClick={() => onToggleRead(message)}
                        data-test-selector="notifications_markRead"
                    >
                        {message.isRead ? translate("Mark as Unread") : translate("Mark as Read")}
                    </Link>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default NotificationItem;
